/**
 * Cashout Utility Module for StarBet WebApp
 * 
 * Supports both 2-Runner and 3-Runner markets for "Bookmaker" (bhav / 100)
 * and "MATCH_ODDS" / decimal odds (odds - 1).
 * 
 * Calculates the exact hedge stake to equalize profit/loss across all runners
 * (maximizing guaranteed profit / minimizing loss).
 */

/**
 * Converts odds/bhav to decimal rate (r)
 * @param {number|string} odds 
 * @param {string} marketType - "Bookmaker" or "MATCH_ODDS"
 * @returns {number}
 */
export const getRate = (odds, marketType) => {
  const num = Number(odds || 0);
  if (num <= 0) return 0;
  return marketType === "Bookmaker" ? num / 100 : num - 1;
};

/**
 * Calculates Cashout Bet & Projected Books using marketId
 * 
 * @param {string} marketId - e.g. "1.23456789" or "1.23456789B"
 * @param {Object} context - { eventDetail, updatedAllRunners, eventLock }
 * @returns {Object|null} cashoutDetails
 */
export const calculateCashout = (marketId, { eventDetail, updatedAllRunners, eventLock }) => {
  if (!marketId) return null;
  if (eventLock === false) return null; // If event is locked/suspended, cannot cashout

  // 1. Locate market by marketId in eventDetail
  const market = eventDetail?.find((m) => m.MarketId === marketId);
  if (!market || !market.runners || market.runners.length < 2) return null;

  const marketType = market.MarketType; // e.g. "Bookmaker" or "MATCH_ODDS"

  // 2. Build list of runners with current book and live back/lay odds
  const runners = market.runners.map((runner) => {
    const matchingMarket = updatedAllRunners?.find(
      (m) => m?.marketId === (runner?.marketId || market?.MarketId)
    );

    let matchingRunner2 = null;
    if (marketType === "Bookmaker") {
      matchingRunner2 = matchingMarket?.runners?.find(
        (r2) =>
          r2?.RunnerName?.replace(/\./g, "").trim().toLowerCase() ===
          runner?.RunnerName?.replace(/\./g, "").trim().toLowerCase()
      );
    } else {
      matchingRunner2 = matchingMarket?.runners?.find(
        (r2) =>
          r2?.selectionId?.toString() === runner?.selectionId?.toString()
      );
    }

    const isSuspended =
      matchingRunner2?.status?.toUpperCase() === "SUSPENDED" ||
      matchingRunner2?.status?.toUpperCase() === "CLOSED";
    const isActive =
      matchingRunner2 &&
      !isSuspended &&
      (matchingRunner2?.status
        ? matchingRunner2?.status?.toUpperCase() === "ACTIVE" ||
          matchingRunner2?.status?.toUpperCase() === "OPEN"
        : true);

    const backPrice = isActive
      ? Number(matchingRunner2?.ex?.availableToBack?.[0]?.price || 0)
      : 0;
    const layPrice = isActive
      ? Number(matchingRunner2?.ex?.availableToLay?.[0]?.price || 0)
      : 0;

    return {
      id: runner.id,
      selectionId: runner.selectionId,
      RunnerName: runner.RunnerName,
      book: Number(runner.book || 0),
      backPrice,
      layPrice,
      rawRunner: runner,
      matchingRunner2,
    };
  });

  const books = runners.map((r) => r.book);
  const maxBook = Math.max(...books);
  const minBook = Math.min(...books);

  // If all runners already have equal book (or all 0), no cashout is needed
  if (Math.abs(maxBook - minBook) < 0.01) return null;

  // =========================================================================
  // UNIVERSAL CASHOUT EVALUATOR (2-Runner and Multi-Runner Markets)
  // Evaluates every runner's Back and Lay to maximize guaranteed book across all outcomes
  // =========================================================================
  let bestOption = null;

  // Maximum allowed odds threshold: Bookmaker bhav <= 100, Match Odds / other <= 1.98
  const maxAllowedOdds = marketType === "Bookmaker" ? 100 : 1.98;

  runners.forEach((runner, idx) => {
    const bookI = runner.book;
    const otherRunners = runners.filter((_, j) => j !== idx);
    const minOtherBook = Math.min(...otherRunners.map((r) => r.book));

    // Option A: BACK this runner (when this runner's book is lower than other runners)
    if (bookI < minOtherBook) {
      const backOdds = runner.backPrice;
      const backRate = getRate(backOdds, marketType);
      if (backOdds > 0 && backOdds <= maxAllowedOdds && backRate > 0) {
        const stake = Number(
          ((minOtherBook - bookI) / (1 + backRate)).toFixed(2)
        );
        const profit = Number((stake * backRate).toFixed(2));
        const liability = stake;

        const projectedBook = {};
        let worstBookAfter = Infinity;

        runners.forEach((r, j) => {
          const newB =
            j === idx
              ? Number((r.book + profit).toFixed(2))
              : Number((r.book - stake).toFixed(2));
          projectedBook[r.selectionId] = newB;
          if (newB < worstBookAfter) worstBookAfter = newB;
        });

        if (
          !bestOption ||
          worstBookAfter > bestOption.minGuaranteedBook ||
          (Math.abs(worstBookAfter - bestOption.minGuaranteedBook) < 0.05 &&
            backOdds < bestOption.odds)
        ) {
          bestOption = {
            marketId: market.MarketId,
            marketType,
            market,
            runner: runner.rawRunner,
            matchingRunner2: runner.matchingRunner2,
            selectionId: runner.selectionId,
            runnerName: runner.RunnerName,
            betType: "Back",
            typeOfPlaceBet: 0,
            odds: backOdds,
            stake,
            profit,
            liability,
            minGuaranteedBook: worstBookAfter,
            projectedBook,
          };
        }
      }
    }

    // Option B: LAY this runner (when this runner's book is greater than other runners)
    if (bookI > minOtherBook) {
      const layOdds = runner.layPrice;
      const layRate = getRate(layOdds, marketType);
      if (layOdds > 0 && layOdds <= maxAllowedOdds && layRate > 0) {
        const stake = Number(
          ((bookI - minOtherBook) / (1 + layRate)).toFixed(2)
        );
        const profit = stake;
        const liability = Number((stake * layRate).toFixed(2));

        const projectedBook = {};
        let worstBookAfter = Infinity;

        runners.forEach((r, j) => {
          const newB =
            j === idx
              ? Number((r.book - liability).toFixed(2))
              : Number((r.book + stake).toFixed(2));
          projectedBook[r.selectionId] = newB;
          if (newB < worstBookAfter) worstBookAfter = newB;
        });

        if (
          !bestOption ||
          worstBookAfter > bestOption.minGuaranteedBook ||
          (Math.abs(worstBookAfter - bestOption.minGuaranteedBook) < 0.05 &&
            layOdds < bestOption.odds)
        ) {
          bestOption = {
            marketId: market.MarketId,
            marketType,
            market,
            runner: runner.rawRunner,
            matchingRunner2: runner.matchingRunner2,
            selectionId: runner.selectionId,
            runnerName: runner.RunnerName,
            betType: "Lay",
            typeOfPlaceBet: 1,
            odds: layOdds,
            stake,
            profit,
            liability,
            minGuaranteedBook: worstBookAfter,
            projectedBook,
          };
        }
      }
    }
  });

  return bestOption;
};

export default calculateCashout;
